import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { Compass, Highlighter, LayoutPanelLeft } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";

export const KeyboardShortcutsDialog = () => {
  const isVisible = useAppState((state) => state.keyboardShortcutsVisible);
  const dispatch = useAppDispatch();

  useHotkeys(
    "esc",
    () => {
      if (isVisible) {
        dispatch({ type: "TOGGLE_KEYBOARD_SHORTCUTS" });
      }
    },
    [isVisible]
  );

  if (!isVisible) {
    return null;
  }

  return (
    <Dialog
      open={isVisible}
      onOpenChange={() => dispatch({ type: "TOGGLE_KEYBOARD_SHORTCUTS" })}
    >
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Master these keyboard shortcuts to navigate even faster.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-8 mt-4">
          <div className="flex flex-row gap-8 justify-center">
            {/* Navigation */}
            <div className="flex-1 bg-muted/40 rounded-lg p-6 border border-border">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-100 text-blue-600 rounded p-2">
                  <Compass className="w-5 h-5" />
                </div>
                <span className="font-semibold text-lg">Navigation</span>
              </div>
              <div className="flex flex-col gap-3">
                <ShortcutRow label="Move between paragraphs" keys={["↑/↓"]} />
                <ShortcutRow
                  label="Jump to previous/next section"
                  keys={["⌘/Ctrl + ←/→"]}
                />
                <ShortcutRow
                  label="Jump to previous/next highlight"
                  keys={["⌥/Alt + ↑/↓"]}
                />
              </div>
            </div>
            {/* Annotations */}
            <div className="flex-1 bg-muted/40 rounded-lg p-6 border border-border">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-100 text-blue-600 rounded p-2">
                  <Highlighter className="w-5 h-5" />
                </div>
                <span className="font-semibold text-lg">Annotations</span>
              </div>
              <div className="flex flex-col gap-3">
                <ShortcutRow label="Highlight green (positive)" keys={["a"]} />
                <ShortcutRow label="Highlight blue (neutral)" keys={["s"]} />
                <ShortcutRow label="Highlight red (negative)" keys={["d"]} />
                <ShortcutRow label="Remove highlight" keys={["backspace"]} />
                <ShortcutRow label="Add text note" keys={["n"]} />
              </div>
            </div>
            {/* Interface */}
            <div className="flex-1 bg-muted/40 rounded-lg p-6 border border-border">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-100 text-blue-600 rounded p-2">
                  <LayoutPanelLeft className="w-5 h-5" />
                </div>
                <span className="font-semibold text-lg">Interface</span>
              </div>
              <div className="flex flex-col gap-3">
                <ShortcutRow label="Toggle right sidebar" keys={["]"]} />
                <ShortcutRow
                  label="Toggle focus mode"
                  keys={["⌘/Ctrl + Shift + F"]}
                />
                <ShortcutRow
                  label="Show keyboard shortcuts"
                  keys={["⌘/Ctrl + /"]}
                />
                <ShortcutRow label="Open scratchpad" keys={["⌘/Ctrl + D"]} />
                <ShortcutRow label="Side panel (TOC)" keys={["1"]} />
                <ShortcutRow label="Side panel (references)" keys={["2"]} />
                <ShortcutRow label="Side panel (notes)" keys={["3"]} />
                <ShortcutRow label="Reader view" keys={["⌘/Ctrl + 1"]} />
                <ShortcutRow label="Rubric view" keys={["⌘/Ctrl + 2"]} />
                <ShortcutRow label="PDF view" keys={["⌘/Ctrl + 3"]} />
              </div>
            </div>
          </div>
          <div className="text-center text-muted-foreground text-sm mt-4">
            Press <kbd className="kbd">⌘/Ctrl + /</kbd> anytime to show this
            cheatsheet
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ShortcutRow = ({ label, keys }: { label: string; keys: string[] }) => {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm">{label}</span>
      <span className="flex gap-1">
        {keys.map((key, i) => (
          <kbd
            key={i}
            className="kbd px-2 py-1 bg-muted rounded border text-xs font-mono whitespace-nowrap"
          >
            {key}
          </kbd>
        ))}
      </span>
    </div>
  );
};
